import { opportunityService } from './OpportunityService'
import { round2 } from './storage'
import type {
  CrmDashboardCards,
  CrmStatistics,
  Opportunity,
  OpportunityFilters,
  OpportunityStage,
} from './types'
import { ALL_STAGES, LOST_REASON_LABELS, PIPELINE_STAGES, STAGE_LABELS } from './types'

/**
 * StatisticsService — CRM KPIs, funnel and rankings.
 */
export class StatisticsService {
  compute(filters: OpportunityFilters = {}, productHints: Array<{ name: string; value: number }> = []): CrmStatistics {
    const items = opportunityService.search(filters)
    const cards = this.cards(items)
    const approved = items.filter((o) => o.stage === 'APPROVED')
    const lost = items.filter((o) => o.stage === 'LOST')
    const closed = [...approved, ...lost]
    const conversionRate =
      closed.length > 0 ? round2((approved.length / closed.length) * 100) : 0
    const averageTicket =
      approved.length > 0
        ? round2(approved.reduce((s, o) => s + o.expectedValue, 0) / approved.length)
        : 0
    const averageCycleDays =
      approved.length > 0
        ? round2(
            approved.reduce((s, o) => {
              const start = new Date(o.createdAt).getTime()
              const end = new Date(o.updatedAt).getTime()
              return s + Math.max(0, (end - start) / (1000 * 60 * 60 * 24))
            }, 0) / approved.length,
          )
        : 0
    const lostValue = round2(lost.reduce((s, o) => s + o.expectedValue, 0))
    const recoveredValue = round2(approved.reduce((s, o) => s + o.expectedValue, 0))

    return {
      cards,
      conversionRate,
      averageTicket,
      averageCycleDays,
      lostValue,
      recoveredValue,
      funnel: this.funnel(items),
      lossesByReason: this.losses(lost),
      ranking: this.rankings(items, productHints),
    }
  }

  cards(items: Opportunity[]): CrmDashboardCards {
    const open = items.filter((o) => !['LOST', 'CANCELLED'].includes(o.stage))
    return {
      totalOpportunities: open.length,
      totalValue: round2(open.reduce((s, o) => s + o.expectedValue, 0)),
      awaitingResponse: items.filter((o) => o.stage === 'AWAITING' || o.stage === 'SENT').length,
      approved: items.filter((o) => o.stage === 'APPROVED').length,
      lost: items.filter((o) => o.stage === 'LOST').length,
    }
  }

  private funnel(items: Opportunity[]) {
    const stages: OpportunityStage[] = [...PIPELINE_STAGES, 'LOST', 'CANCELLED']
    return stages
      .filter((stage) => ALL_STAGES.includes(stage))
      .map((stage) => {
        const subset = items.filter((o) => o.stage === stage)
        return {
          stage,
          label: STAGE_LABELS[stage],
          count: subset.length,
          value: round2(subset.reduce((s, o) => s + o.expectedValue, 0)),
        }
      })
  }

  private losses(lost: Opportunity[]) {
    const map = new Map<string, { reason: string; count: number; value: number }>()
    for (const item of lost) {
      const reason = item.lostReasonCode
        ? LOST_REASON_LABELS[item.lostReasonCode]
        : 'Não informado'
      const current = map.get(reason) ?? { reason, count: 0, value: 0 }
      current.count += 1
      current.value = round2(current.value + item.expectedValue)
      map.set(reason, current)
    }
    return [...map.values()].sort((a, b) => b.value - a.value)
  }

  private rankings(items: Opportunity[], productHints: Array<{ name: string; value: number }>) {
    const customers = new Map<string, { name: string; value: number; count: number }>()
    const sellers = new Map<string, { name: string; value: number; count: number }>()

    for (const item of items) {
      const customerName = item.customerName ?? 'Sem cliente'
      const c = customers.get(customerName) ?? { name: customerName, value: 0, count: 0 }
      c.value = round2(c.value + item.expectedValue)
      c.count += 1
      customers.set(customerName, c)

      const seller = item.responsibleUser
      const s = sellers.get(seller) ?? { name: seller, value: 0, count: 0 }
      s.value = round2(s.value + item.expectedValue)
      s.count += 1
      sellers.set(seller, s)
    }

    const products =
      productHints.length > 0
        ? [...productHints]
            .sort((a, b) => b.value - a.value)
            .slice(0, 8)
            .map((p) => ({ name: p.name, value: p.value, count: 1 }))
        : items.slice(0, 5).map((o) => ({
            name: o.quoteCode,
            value: o.expectedValue,
            count: 1,
          }))

    const categories = [
      { name: 'Hardware', value: round2(items.reduce((s, o) => s + o.expectedValue * 0.55, 0)), count: items.length },
      { name: 'Serviços', value: round2(items.reduce((s, o) => s + o.expectedValue * 0.25, 0)), count: items.length },
      { name: 'Software', value: round2(items.reduce((s, o) => s + o.expectedValue * 0.2, 0)), count: items.length },
    ]

    const top = <T extends { value: number }>(list: T[]) =>
      [...list].sort((a, b) => b.value - a.value).slice(0, 8)

    return {
      customers: top([...customers.values()]),
      products: top(products),
      categories: top(categories),
      sellers: top([...sellers.values()]),
    }
  }
}

export const statisticsService = new StatisticsService()
